import { FORMATIONS } from '../data/formations.js';

/**
 * L'historique qui nourrit le pré-remplissage.
 *
 * Il vient de deux sources, et la priorité est donnée à TES compos confirmées.
 *
 * La raison n'est pas de commodité. Aucun fournisseur gratuit ne donne les feuilles de
 * match — vérifié chez quatre d'entre eux et sur l'API officielle de la LFP, qui les
 * garde derrière authentification. Mais l'application n'en a pas besoin : tu poses le
 * onze à chaque journée, c'est son usage principal. Marqué « confirmé », ce onze
 * devient l'historique du match suivant. Au bout de trois journées il vaut mieux
 * qu'une feuille achetée, parce que les postes sont ceux que TU as jugés justes — et
 * rien ne casse quand un site change son HTML.
 */
export function historiqueUtile({ fournisseur = [], matchs = [] } = {}) {
  const miennes = matchs
    // Un onze n'enseigne quelque chose qu'à partir du moment où il a été AFFIRMÉ :
    // annoncé le jour du match, ou joué. Un brouillon de la veille, jamais.
    .filter((c) => (c.etat === 'jouee' || c.etat === 'annoncee') && !c.supprime_a && FORMATIONS[c.formation])
    .map((c) => ({
      matchId: c.id,
      date: c.date ?? new Date(c.maj_a).toISOString().slice(0, 10),
      formation: c.formation,
      adversaire: c.adversaire ?? null,
      domicile: c.domicile !== false,
      score: scoreDe(c.evenements),
      evenements: c.evenements ?? [],
      // Les places sont rangées dans l'ordre des créneaux de leur formation, ce que
      // le pré-remplissage attend : l'index i désigne le i-ᵉ créneau.
      titulaires: ordonner(c),
      maj_a: c.maj_a ?? 0,
      origine: 'moi',
    }))
    .filter((h) => h.titulaires.length === FORMATIONS[h.formation].length);

  const autres = (fournisseur ?? []).map((h) => ({ ...h, origine: h.origine ?? 'fournisseur' }));

  // UNE JOURNÉE NE COMPTE QU'UNE FOIS, ET C'EST LA TIENNE QUI GAGNE.
  //
  // Deux sources décrivent le même match : tes compositions affirmées, et les onze
  // relevés à la main que les skills entretiennent pour les journées que tu n'as pas
  // suivies. Les identifiants diffèrent — un uuid d'un côté, « reel-5 » de l'autre —
  // donc dédoublonner sur l'identifiant ne suffit pas : la même journée apparaîtrait
  // deux fois et pèserait double dans le pré-remplissage.
  //
  // La clé est la RENCONTRE : la date et l'adversaire. Et ce que tu as saisi prime
  // toujours, parce que tu y étais.
  // Sans adversaire, il n'y a pas de rencontre identifiable : on retombe sur
  // l'identifiant, sinon deux matchs anonymes partageraient la même clé et le second
  // effacerait le premier.
  const rencontre = (h) => {
    const adv = String(h.adversaire ?? '').toLowerCase().trim();
    return adv ? `${h.date ?? ''}|${adv}` : `id:${h.matchId}`;
  };
  // Le dédoublonnage vaut AUSSI entre tes propres matchs. Il ne jouait qu'entre les
  // deux sources : deux compositions posées pour la même rencontre — une préparée la
  // veille, une refaite le jour même — pesaient double dans le pré-remplissage. On
  // garde la plus récemment touchée, c'est celle qui reflète ta dernière décision.
  const gardees = new Map();
  for (const h of miennes.sort((a, b) => (b.maj_a ?? 0) - (a.maj_a ?? 0))) {
    const cle = rencontre(h);
    if (!gardees.has(cle)) gardees.set(cle, h);
  }

  const vues = new Set(gardees.keys());
  const tout = [...gardees.values(), ...autres.filter((h) => !vues.has(rencontre(h)))];

  return tout.sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')));
}

function scoreDe(evenements) {
  if (!evenements?.length) return null;
  const nous = evenements.filter((e) => e.type === 'but').length;
  const eux = evenements.filter((e) => e.type === 'butAdverse').length;
  return nous || eux ? `${nous}-${eux}` : null;
}

function ordonner(compo) {
  const places = compo.places ?? [];
  const taille = FORMATIONS[compo.formation]?.length ?? 0;
  const out = new Array(taille).fill(null);
  for (const p of places) {
    if (p.index >= 0 && p.index < taille) out[p.index] = p.joueurId ?? null;
  }
  // Un onze incomplet n'apprend rien : il laisserait des créneaux vides au lieu de
  // proposer quelqu'un, ce qui est pire que de ne pas s'en servir.
  return out.every(Boolean) ? out : [];
}
